import { cloneDeep } from 'lodash-es';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { TeamItem } from '@/models/team';
import { createTeamPageHeaders } from '@/pages/system/team/TeamPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { teamService } from '@/services/teamService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { usePersistentFilters } from '@/stores/composables/usePersistentFilters';
import { IListParams, IPageOptions, ITeamPageFilters, ITeamUpdateResponse } from '@/types';
import type { DataTableHeader } from '@/types/vuetify';

function createTeamStoreSetup(storeId: string) {
    const items = shallowRef<TeamItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    const filters = reactive<ITeamPageFilters>({
        sportId: createRequiredSportFilter(),
        teamCustomTeamId: {
            value: null,
            title: 'team.filters.customTeamId',
            allowSaveState: true,
        },
        teamName: {
            value: null,
            title: 'team.filters.teamName',
            allowSaveState: true,
        },
        bookmakerId: {
            value: null,
            title: 'team.filters.bookmakerId',
            allowSaveState: true,
        },
        teamCustomTeamAssociationStatus: {
            value: null,
            title: 'team.filters.customTeamAssociationStatus',
            allowSaveState: true,
        },
    });

    const hiddenFilters = ref<string[]>([]);
    const visibleFiltersMobile = ref<string[]>([]);
    const filterOrder = ref<string[]>([]);

    const availableFilters = computed(() => {
        const entries = Object.entries(filters).map(([key, meta]) => ({
            key,
            title: meta.title || '',
        }));

        if (filterOrder.value.length > 0) {
            entries.sort((a, b) => {
                const indexA = filterOrder.value.indexOf(a.key);
                const indexB = filterOrder.value.indexOf(b.key);
                return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
            });
        }

        return entries;
    });

    const useButtonForFiltersApply = ref(false);
    const saveFiltersState = ref(false);

    const visibleFilters = computed(() => Object.keys(filters).filter((key) => !hiddenFilters.value.includes(key)));

    const headers = computed<DataTableHeader[]>(() => createTeamPageHeaders());
    const tableHeaders = computed<DataTableHeader[]>(() => createTeamPageHeaders());

    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 100,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [{ key: 'id', order: 'desc' }],
    });

    const persistentFilters = usePersistentFilters({
        filters,
        enabled: computed(() => saveFiltersState.value && storeId === 'team'),
    });

    const reloadFiltersFromStorage = () => {
        resetFilters();
        if (storeId === 'team' && saveFiltersState.value) {
            persistentFilters.loadFromStorage();
        }
    };

    function resetFilters() {
        resetRequiredSportFilter(filters.sportId);
        filters.teamCustomTeamId.value = null;
        filters.teamName.value = null;
        filters.bookmakerId.value = null;
        filters.teamCustomTeamAssociationStatus.value = null;
    }

    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);

            const params: IListParams<Record<string, any>> = {
                filters: filtersToSend,
                options: {
                    sortBy: options.value.sortBy,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Bookmaker', 'Country', 'AuditTable'],
            };

            const response = await teamService.list(params);
            items.value = (response.data.rows || []).map((row) => new TeamItem(row));
            total.value = response.data.total || 0;
        } catch (error) {
            if (isRequestCanceled(error)) return;
            console.error('Error loading teams:', error);
            items.value = [];
            total.value = 0;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
        if (value.sortBy !== undefined) {
            options.value.sortBy = value.sortBy;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    function mergeMutationResponse(response: { data: ITeamUpdateResponse }) {
        if (response.data.success === false) return;
        const row = response.data.row;
        if (!row) return;

        const idx = items.value.findIndex((i) => i.id === row.id);
        if (idx !== -1) {
            const next = [...items.value];
            next[idx] = new TeamItem(row);
            items.value = next;
            triggerRef(items);
        }
    }

    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        loading,
        headers,
        tableHeaders,
        options,
        loadData,
        updateOptions,
        clearData,
        mergeMutationResponse,
        hasRefresh: false,
    };
}

export function useTeamStore(storeId: string) {
    const useStore = defineStore(storeId, () => createTeamStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'team'),
    });
    return useStore();
}
